import React, { useCallback, useEffect, useState, useRef } from 'react'
import { Card, Badge, Form, Button, Alert } from '@themesberg/react-bootstrap'
import { getAllGames, getGameGuide, updateOrderNumber, updateStatus, updateIcon, updateCutoffTime, updatePrintCutoffTime, updateCutoffOffset, uploadIcon } from '../../../../controller/api/game';
import { getLotoIcons, updateLotoIcon, getLotoRegionSetting, updateLotoRegionSetting } from '../../../../controller/api/__loto__/setting';
import { getClassicRegionSetting, updateClassicRegionSetting } from '../../../../controller/api/__classic__/setting';
import { getKenoPrintCutoffSetting, updateKenoPrintCutoffSetting } from '../../../../controller/api/setting';
import { useCookies } from 'react-cookie';
import { Link } from 'react-router-dom';
import UpdateGuide from '../sub-screen/update-guide/UpdateGuide';
import Lotto535Config from '../sub-screen/lotto535-config/Lotto535Config';
import CutoffPolicyDraft from '../sub-screen/cutoff-policy/CutoffPolicyDraft';
import { Routes } from '../../../../routes';
import { CUTOFF_DRAFT_GAMES, LOTO_SETTING_MANAGED_GAMES, isGameEnabled, getGameCutoffSummary } from './game-settings-view';
import './game-settings.css';
import LogoKeno from '../../../../assets/img/logo-keno.png';
import LogoMega from '../../../../assets/img/logo-mega.png';
import LogoPower from '../../../../assets/img/logo-power.png';
import LogoMax3d from '../../../../assets/img/logo-max3d.png';
import LogoMax3dPro from '../../../../assets/img/logo-max3dpro.png';
import LogoLotto535 from '../../../../assets/img/logo-lotto535.png';

const GAME_LOGOS = { keno: LogoKeno, mega: LogoMega, power: LogoPower,
  max3d: LogoMax3d, max3dpro: LogoMax3dPro, lotto535: LogoLotto535 };

const GameLogo = ({ id, name, icon }) => {
  const [failedSources, setFailedSources] = useState([]);
  const source = [icon, GAME_LOGOS[id]].find(value => value && !failedSources.includes(value));
  return source ? <img className="game-setting-logo" src={source} alt={`Logo ${name}`}
    onError={() => setFailedSources(previous => [...previous, source])} /> :
    <span className="game-setting-logo game-setting-logo-placeholder" aria-hidden="true">{id.slice(0, 2).toUpperCase()}</span>;
};

const IconEditor = ({ value, onChange, onUpload }) => {
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)

  const handleRemove = () => {
    if (!value) return
    if (!window.confirm('Xoá hình icon hiện tại?')) return
    onChange('')
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const url = await onUpload(file)
      if (!url) alert('Upload thất bại')
    } catch (err) {
      console.log('upload icon error', err)
      alert('Upload thất bại')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="game-setting-icon-editor">
      {value && (
        <img
          src={value}
          alt=""
          style={{ width: 36, height: 24, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4 }}
          onError={(e) => { e.target.style.visibility = 'hidden' }}
        />
      )}
      <Button
        variant="outline-primary"
        size="sm"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
      >
        {uploading ? 'Đang tải...' : 'Tải logo'}
      </Button>
      {value && (
        <Button
          variant="outline-danger"
          size="sm"
          disabled={uploading}
          onClick={handleRemove}
        >
          Xoá
        </Button>
      )}
      <Form.Control
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
    </div>
  )
}

const CutoffEditor = ({ value, onChange, label }) => {
  const [time, setTime] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setTime(value || '')
  }, [value])

  const dirty = time !== (value || '')

  const save = async () => {
    setError('')
    setSaving(true)
    try { await onChange(time) }
    catch (failure) { setError(failure?.response?.data?.message || failure.message || 'Không lưu được cấu hình') }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div className="game-setting-editor">
      <Form.Control
        type="time"
        aria-label={label}
        disabled={saving}
        value={time}
        onChange={(e) => setTime(e.target.value)}
      />
      <Button variant={dirty ? 'primary' : 'outline-secondary'} disabled={!dirty || saving} onClick={save}>
        {saving ? '...' : 'Lưu'}
      </Button>
      </div>
      {error && <p role="alert" className="text-danger small mt-2 mb-0">{error}</p>}
    </div>
  )
}

// Game quay nhiều kỳ/ngày: chốt theo "số giây trước mỗi kỳ" thay vì 1 mốc giờ HH:mm.
const MAX_CUTOFF_OFFSET_SECONDS = 420

const CutoffOffsetEditor = ({ value, onChange, placeholder = 'VD 30', unitLabel = 'giây trước giờ xổ', label, disabled = false }) => {
  const [seconds, setSeconds] = useState(value == null ? '' : String(value))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setSeconds(value == null ? '' : String(value))
  }, [value])

  const dirty = seconds !== (value == null ? '' : String(value))

  const save = async () => {
    setError('')
    setSaving(true)
    try { await onChange(seconds) }
    catch (failure) { setError(failure?.response?.data?.message || failure.message || 'Không lưu được cấu hình') }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div className="game-setting-editor">
      <Form.Control
        type="number"
        aria-label={label}
        disabled={disabled || saving}
        min={0}
        max={MAX_CUTOFF_OFFSET_SECONDS}
        placeholder={placeholder}
        value={seconds}
        onChange={(e) => setSeconds(e.target.value)}
      />
      <span className="text-muted">{unitLabel}</span>
      <Button variant={dirty ? 'primary' : 'outline-secondary'} disabled={disabled || !dirty || saving} onClick={save}>
        {saving ? '...' : 'Lưu'}
      </Button>
      </div>
      {error && <p role="alert" className="text-danger small mt-2 mb-0">{error}</p>}
    </div>
  )
}

const OrderNumberEditor = ({ id, value, onChange }) => {
  const [order, setOrder] = useState(value == null ? '' : String(value))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setOrder(value == null ? '' : String(value)) }, [value])
  const save = async () => {
    setError('')
    if (order.trim() === '' || !Number.isSafeInteger(Number(order))) {
      setError('Thứ tự phải là số nguyên')
      return
    }
    setSaving(true)
    try { await onChange(Number(order), id) }
    catch (failure) { setError(failure?.response?.data?.message || failure.message || 'Không lưu được thứ tự') }
    finally { setSaving(false) }
  }
  return <div>
    <Form.Label htmlFor={`game-order-${id}`}>Thứ tự hiển thị</Form.Label>
    <div className="game-setting-editor">
      <Form.Control size="sm" id={`game-order-${id}`} type="number" step="1" value={order}
        disabled={saving} onChange={event => setOrder(event.target.value)} />
      <Button size="sm" variant="outline-primary" disabled={saving || order === (value == null ? '' : String(value))} onClick={save}>
        {saving ? '…' : 'Lưu'}
      </Button>
    </div>
    {error && <p role="alert" className="text-danger small mt-2 mb-0">{error}</p>}
  </div>
}

export const GameSettingsRow = (props) => {
  const { name, gameType, jackpot1, jackpot2, orderNumber, onStatusChange, onOrderNumberChange, id,
    onGuidePress, onLotto535ConfigPress, icon, onIconChange, onIconUpload, cutoffTime, onCutoffChange,
    printCutoffTime, onPrintCutoffChange, cutoffOffsetSeconds, onCutoffOffsetChange,
    kenoPrintCutoffSeconds, onKenoPrintCutoffChange, expanded, onToggle } = props;
  const isLotoSettingManaged = LOTO_SETTING_MANAGED_GAMES.includes(id);
  const hasVietlottPrintCutoff = gameType === 'VIETLOTT' && id !== 'lotto535' && !isLotoSettingManaged;
  const enabled = isGameEnabled(props);
  const supportsCutoffDraft = CUTOFF_DRAFT_GAMES.includes(id);
  const summary = getGameCutoffSummary(props, kenoPrintCutoffSeconds);

  return (
    <article className="game-setting" aria-labelledby={`game-name-${id}`}>
      <div className="game-setting-overview">
        <div className="game-setting-identity">
          <GameLogo key={`${id}:${icon || ''}`} id={id} name={name} icon={icon} />
          <div className="game-setting-name">
            <h6 id={`game-name-${id}`}>{name}</h6>
            <small className="text-muted">{id}</small>
            {supportsCutoffDraft && <small className="game-setting-policy-source text-muted">
              {props.cutoffPolicyEnabled ? 'Đang áp dụng 3 mốc' : 'Đang dùng cấu hình cũ'}
            </small>}
            {supportsCutoffDraft && <Button size="sm" variant="primary" className="game-setting-edit-cutoffs"
              aria-label={`Chỉnh 3 mốc ${name}`} onClick={() => props.onCutoffDraftPress({ id, name })}>
              Chỉnh 3 mốc
            </Button>}
          </div>
        </div>
        <dl className="game-setting-cutoffs">
          {[
            ['sale', '1. Đóng mua'],
            ['print', '2. Đóng in'],
            ['upload', '3. Đóng upload vé'],
          ].map(([key, label]) => <div key={key}>
            <dt>{label}</dt><dd>{summary[key].value}</dd>
          </div>)}
        </dl>
        <div className="game-setting-actions">
          <Form.Check
            type="switch"
            id={`status-switch-${id}`}
            checked={enabled}
            onChange={evt => onStatusChange(evt.target.checked ? 1 : 0, id)}
            label={id === 'lotto535' ? (enabled ? 'Bật' : 'Tắt') : (enabled ? 'Hiển thị' : 'Ẩn')}
            aria-label={`Trạng thái ${name}`}
          />
          <Button size="sm" variant={expanded ? 'primary' : 'outline-primary'}
            aria-expanded={expanded} aria-controls={`game-config-${id}`}
            aria-label={`Cấu hình ${name}`} onClick={onToggle}>
            {expanded ? 'Thu gọn' : supportsCutoffDraft ? 'Thiết lập khác' : 'Cấu hình'}
          </Button>
        </div>
      </div>
      <div className="game-setting-panel" id={`game-config-${id}`} hidden={!expanded}>
        {supportsCutoffDraft ? <p className="small text-muted mb-3">
          Ba mốc đang dùng được hiển thị ở hàng phía trên. Bấm <strong>Chỉnh 3 mốc</strong> cạnh tên game để thay đổi.
        </p> : <>
        <div className="mb-3"><Badge bg="success">Cấu hình đang dùng</Badge>
          <small className="text-muted ms-2">Giờ cố định hiển thị theo giờ Việt Nam (24 giờ).</small>
        </div>
        <div className="game-setting-fields">
          <section aria-label={`Đóng mua ${name}`}>
            <h6>1. Đóng mua</h6>
            <p className="text-muted small">Mốc ngừng nhận mua vé của khách.</p>
            {id === 'lotto535' ? (
              <Button size="sm" variant="outline-primary" onClick={onLotto535ConfigPress}>Mở cấu hình Lotto 5/35</Button>
            ) : isLotoSettingManaged ? (
              <Button as={Link} to={Routes.LotoSetting.path} size="sm" variant="outline-primary">Mở cài đặt Lô tô</Button>
            ) : id === 'keno' ? (
              <CutoffOffsetEditor label={`Đóng mua ${name}`} value={cutoffOffsetSeconds}
                onChange={val => onCutoffOffsetChange(id, val)} />
            ) : (
              <CutoffEditor label={`Đóng mua ${name}`} value={cutoffTime}
                onChange={val => onCutoffChange(id, val)} />
            )}
            <small className="text-muted d-block mt-2">{summary.sale.note}</small>
          </section>
          <section aria-label={`Đóng in ${name}`}>
            <h6>2. Đóng in</h6>
            <p className="text-muted small">Mốc ngừng gửi lệnh in, khác mốc upload ảnh.</p>
            {id === 'lotto535' ? (
              <Button size="sm" variant="outline-primary" onClick={onLotto535ConfigPress}>Mở cấu hình Lotto 5/35</Button>
            ) : id === 'keno' ? (
              <CutoffOffsetEditor label={`Đóng in ${name}`} value={kenoPrintCutoffSeconds}
                onChange={onKenoPrintCutoffChange} placeholder="VD 1" disabled={kenoPrintCutoffSeconds == null} />
            ) : hasVietlottPrintCutoff ? (
              <CutoffEditor label={`Đóng in ${name}`} value={printCutoffTime || cutoffTime}
                onChange={val => onPrintCutoffChange(id, val)} />
            ) : <p className="small mb-0">Không có ô cấu hình in riêng tại màn này.</p>}
            <small className="text-muted d-block mt-2">{summary.print.note}</small>
          </section>
          <section aria-label={`Đóng upload ${name}`}>
            <h6>3. Đóng upload vé</h6>
            <p className="text-muted small">Mốc gắn ảnh vé / xác nhận hoàn thành đơn.</p>
            <p className="small mb-0"><strong>{summary.upload.value}</strong></p>
            <small className="text-muted d-block mt-2">{summary.upload.note}</small>
          </section>
        </div>
        </>}
        {supportsCutoffDraft && !props.cutoffPolicyEnabled && id !== 'lotto535' && <details className="game-setting-legacy">
          <summary>Chỉnh riêng cấu hình cũ (tùy chọn)</summary>
          <p className="small text-muted mt-2">Các ô này giữ cách cấu hình cũ. Để cấu hình đủ mua, in và upload tại một nơi, dùng nút “Chỉnh 3 mốc”.</p>
          <div className="game-setting-fields">
            <section><h6>Đóng mua — cấu hình cũ</h6>
              {id === 'keno' ? <CutoffOffsetEditor label={`Đóng mua cũ ${name}`} value={cutoffOffsetSeconds}
                onChange={val => onCutoffOffsetChange(id, val)} /> :
                <CutoffEditor label={`Đóng mua cũ ${name}`} value={cutoffTime} onChange={val => onCutoffChange(id, val)} />}
            </section>
            {hasVietlottPrintCutoff && <section><h6>Đóng in — cấu hình cũ</h6>
              {id === 'keno' ? <CutoffOffsetEditor label={`Đóng in cũ ${name}`} value={kenoPrintCutoffSeconds}
                onChange={onKenoPrintCutoffChange} placeholder="VD 1" disabled={kenoPrintCutoffSeconds == null} /> :
                <CutoffEditor label={`Đóng in cũ ${name}`} value={printCutoffTime || cutoffTime}
                  onChange={val => onPrintCutoffChange(id, val)} />}
            </section>}
          </div>
        </details>}
        {id === 'lotto535' && <Button size="sm" className="mb-3" variant="outline-primary" onClick={onLotto535ConfigPress}>Mở cấu hình Lotto 5/35</Button>}
        <div className="game-setting-display">
          <div><h6>Logo trò chơi</h6>
            <IconEditor value={icon} onChange={val => onIconChange(id, val)} onUpload={file => onIconUpload(id, file)} />
          </div>
          <OrderNumberEditor id={id} value={orderNumber} onChange={onOrderNumberChange} />
          <div><h6>Jackpot</h6>
            {jackpot1 ? <div className="text-success">{new Intl.NumberFormat('vi-VN').format(jackpot1)} đ</div> : <small className="text-muted">—</small>}
            {jackpot2 ? <div className="text-info small">{new Intl.NumberFormat('vi-VN').format(jackpot2)} đ</div> : null}
          </div>
          <div><h6>Nội dung</h6>
            <Button size="sm" variant="outline-primary" onClick={() => onGuidePress(id, name)}>Hướng dẫn</Button>
          </div>
        </div>
      </div>
    </article>
  );
};

const LOTO_REGIONS = [
  { code: 'MB', label: 'Lô Tô Miền Bắc' },
  { code: 'MT', label: 'Lô Tô Miền Trung' },
  { code: 'MN', label: 'Lô Tô Miền Nam' },
]

const CLASSIC_REGIONS = [
  { code: 'MB', label: 'Xổ Số Kiến Thiết Miền Bắc' },
  { code: 'MT', label: 'Xổ Số Kiến Thiết Miền Trung' },
  { code: 'MN', label: 'Xổ Số Kiến Thiết Miền Nam' },
]

const ClassicRegionRow = ({ code, label, enabled, onStatusChange, disabled }) => (
  <article>
    <h6>{label}</h6><small className="text-muted">Miền {code}</small>
    <Form.Select aria-label={`Trạng thái XSKT ${code}`} disabled={disabled}
      value={enabled ? 1 : 0} onChange={evt => onStatusChange(code, evt.target.value === '1')}>
      <option value={0}>Ẩn khỏi trang chủ</option>
      <option value={1}>Hiển thị trên trang chủ</option>
    </Form.Select>
  </article>
);

const LotoIconRow = ({ code, label, icon, enabled, onIconChange, onIconUpload, onStatusChange, disabled }) => (
  <article>
    <h6>{label}</h6><small className="text-muted d-block mb-2">Miền {code}</small>
    <IconEditor value={icon} onChange={val => onIconChange(code, val)} onUpload={file => onIconUpload(code, file)} />
    <Form.Select aria-label={`Trạng thái Lô tô ${code}`} disabled={disabled}
      value={enabled ? 1 : 0} onChange={evt => onStatusChange(code, evt.target.value === '1')}>
      <option value={0}>Ẩn khỏi trang chủ</option>
      <option value={1}>Hiển thị trên trang chủ</option>
    </Form.Select>
  </article>
);

export const GameTable = () => {

  const [cookies] = useCookies(['lottmekong'])

  const [listGame, setListGame] = useState({})
  const [lotoIcons, setLotoIcons] = useState({ MB: '', MT: '', MN: '' })
  const [lotoRegionConfig, setLotoRegionConfig] = useState({ MB: false, MT: false, MN: false })
  const [lotoCutoffTimes, setLotoCutoffTimes] = useState({})
  const [classicRegionConfig, setClassicRegionConfig] = useState({ MB: true, MT: true, MN: true })
  const [kenoPrintCutoffSeconds, setKenoPrintCutoffSeconds] = useState(null)
  const [cutoffDraftGame, setCutoffDraftGame] = useState(null)
  const [expandedGameId, setExpandedGameId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [lotoRegionReady, setLotoRegionReady] = useState(false)
  const [classicRegionReady, setClassicRegionReady] = useState(false)

  const [modalShow, setModalShow] = useState(false);
  const [lotto535ConfigShow, setLotto535ConfigShow] = useState(false);
  const [lotto535ConfigVersion, setLotto535ConfigVersion] = useState(0);
  const [currentGameName, setCurrentGameName] = useState('');
  const [currentGameId, setCurrentGameId] = useState('');
  const [currentGameGuide, setCurrentGameGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);

  const fetchGames = useCallback(async () => {
    const list = await getAllGames(cookies.lottmekong)
    if (Array.isArray(list?.data)) {
      setListGame({
        data: list.data,
        total: list.total,
        isOff: list.data.filter(item => !isGameEnabled(item)).length,
        isOn: list.data.filter(item => isGameEnabled(item)).length,
      })
    } else {
      throw new Error('Không tải được danh sách trò chơi')
    }
  }, [cookies.lottmekong])

  const fetchLotoIcons = useCallback(async () => {
    const res = await getLotoIcons(cookies.lottmekong)
    if (res?.icons) {
      setLotoIcons(res.icons)
    }
  }, [cookies.lottmekong])

  const fetchLotoRegionConfig = useCallback(async () => {
    const res = await getLotoRegionSetting(cookies.lottmekong)
    if (res?.config) {
      setLotoRegionConfig({
        MB: !!res.config.MB,
        MT: !!res.config.MT,
        MN: !!res.config.MN,
      })
      setLotoCutoffTimes(res.cutoffTimes || {})
      setLotoRegionReady(true)
    } else {
      throw new Error('Không tải được cấu hình miền Lô tô')
    }
  }, [cookies.lottmekong])

  const fetchClassicRegionConfig = useCallback(async () => {
    const res = await getClassicRegionSetting(cookies.lottmekong)
    if (res?.config) {
      setClassicRegionConfig({
        MB: !!res.config.MB,
        MT: !!res.config.MT,
        MN: !!res.config.MN,
      })
      setClassicRegionReady(true)
    } else {
      throw new Error('Không tải được cấu hình miền XSKT')
    }
  }, [cookies.lottmekong])

  const fetchKenoPrintCutoff = useCallback(async () => {
    const seconds = await getKenoPrintCutoffSetting(cookies.lottmekong)
    if (seconds != null) {
      setKenoPrintCutoffSeconds(seconds)
    } else {
      throw new Error('Không tải được giờ đóng in Keno')
    }
  }, [cookies.lottmekong])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError('')
    setLotoRegionReady(false)
    setClassicRegionReady(false)
    setKenoPrintCutoffSeconds(null)
    Promise.allSettled([
      fetchGames(), fetchLotoIcons(), fetchLotoRegionConfig(),
      fetchClassicRegionConfig(), fetchKenoPrintCutoff(),
    ]).then(results => {
      if (cancelled) return
      const failures = results.filter(result => result.status === 'rejected')
      if (failures.length) setLoadError(failures.map(result =>
        result.reason?.response?.data?.message || result.reason?.message || 'Không tải đủ cấu hình').join('; '))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [fetchGames, fetchLotoIcons, fetchLotoRegionConfig, fetchClassicRegionConfig, fetchKenoPrintCutoff, refreshKey])

  const onGuidePress = async (id, name) => {
    setCurrentGameId(id)
    setCurrentGameName(name)
    setCurrentGameGuide(null)
    setGuideLoading(true)
    setModalShow(true)
    try {
      const guide = await getGameGuide(cookies.lottmekong, id)
      setCurrentGameGuide(guide)
    } catch (err) {
      setModalShow(false)
      alert(err?.response?.data?.message || 'Không tải được hướng dẫn trò chơi')
    } finally {
      setGuideLoading(false)
    }
  }

  const onStatusChange = async (newStatus, id) => {
    // Cập nhật ngay (switch nhanh, không reload trang)
    setListGame(prev => {
      if (!prev.data) return prev
      const data = prev.data.map(g => g.id === id ? {
        ...g,
        status: newStatus,
        ...(id === 'lotto535' ? { publicEnabled: newStatus === 1 } : {}),
      } : g)
      return {
        ...prev,
        data,
        isOff: data.filter(item => !isGameEnabled(item)).length,
        isOn: data.filter(item => isGameEnabled(item)).length,
      }
    })
    try {
      const resp = await updateStatus(cookies.lottmekong, id, newStatus)
      if (!resp) throw new Error('Không cập nhật được trạng thái game')
      if (id === 'lotto535') {
        setLotto535ConfigVersion((current) => current + 1)
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Có lỗi xảy ra')
      fetchGames()
    }
  }

  const onOrderNumberChange = async (newOrderNumber, id) => {
    const resp = await updateOrderNumber(cookies.lottmekong, id, newOrderNumber)
    if (resp) {
      setListGame(prev => ({ ...prev,
        data: prev.data?.map(game => game.id === id ? { ...game, orderNumber: newOrderNumber } : game),
      }))
    } else {
      throw new Error('Không lưu được thứ tự hiển thị')
    }
  }

  const onIconChange = async (id, icon) => {
    await updateIcon(cookies.lottmekong, id, icon)
  }

  const onCutoffChange = async (id, cutoffTime) => {
    const resp = await updateCutoffTime(cookies.lottmekong, id, cutoffTime)
    if (resp) {
      setListGame(prev => ({
        ...prev,
        data: prev.data?.map(g => g.id === id ? { ...g, cutoffTime: cutoffTime || null } : g),
      }))
    } else {
      throw new Error('Không lưu được giờ đóng mua')
    }
  }

  const onPrintCutoffChange = async (id, printCutoffTime) => {
    const resp = await updatePrintCutoffTime(cookies.lottmekong, id, printCutoffTime)
    if (resp) {
      setListGame(prev => ({
        ...prev,
        data: prev.data?.map(g => g.id === id ? { ...g, printCutoffTime: printCutoffTime || null } : g),
      }))
    } else {
      throw new Error('Không lưu được giờ đóng in')
    }
  }

  const onCutoffOffsetChange = async (id, seconds) => {
    const value = seconds === '' || seconds == null ? null : parseInt(seconds, 10)
    const resp = await updateCutoffOffset(cookies.lottmekong, id, value)
    if (resp) {
      setListGame(prev => ({
        ...prev,
        data: prev.data?.map(g => g.id === id ? { ...g, cutoffOffsetSeconds: value } : g),
      }))
    } else {
      throw new Error('Không lưu được mốc đóng mua')
    }
  }

  const onKenoPrintCutoffChange = async (seconds) => {
    const value = seconds === '' || seconds == null ? 0 : parseInt(seconds, 10)
    const saved = await updateKenoPrintCutoffSetting(cookies.lottmekong, value)
    if (saved != null) {
      setKenoPrintCutoffSeconds(saved)
    } else {
      throw new Error('Không lưu được mốc đóng in Keno')
    }
  }

  const onIconUpload = async (id, file) => {
    const resp = await uploadIcon(cookies.lottmekong, 'game', id, file)
    if (resp && resp.url) {
      setListGame(prev => ({
        ...prev,
        data: prev.data?.map(g => g.id === id ? { ...g, icon: resp.url } : g),
      }))
      return resp.url
    }
    return null
  }

  const onLotoIconChange = async (region, icon) => {
    await updateLotoIcon(cookies.lottmekong, region, icon)
  }

  const onLotoStatusChange = async (region, enabled) => {
    const nextConfig = { ...lotoRegionConfig, [region]: enabled }
    const previous = lotoRegionConfig
    setLotoRegionConfig(nextConfig)
    const res = await updateLotoRegionSetting(cookies.lottmekong, {
      ...nextConfig,
      cutoffTimes: lotoCutoffTimes,
    })
    if (!res || res.status !== 1) {
      setLotoRegionConfig(previous)
      alert('Có lỗi xảy ra')
    } else if (res.cutoffTimes) {
      setLotoCutoffTimes(res.cutoffTimes)
    }
  }

  const onClassicStatusChange = async (region, enabled) => {
    const nextConfig = { ...classicRegionConfig, [region]: enabled }
    const previous = classicRegionConfig
    setClassicRegionConfig(nextConfig)
    const res = await updateClassicRegionSetting(cookies.lottmekong, nextConfig)
    if (!res || res.status !== 1) {
      setClassicRegionConfig(previous)
      alert('Có lỗi xảy ra')
    }
  }

  const onLotoIconUpload = async (region, file) => {
    const resp = await uploadIcon(cookies.lottmekong, 'loto', region, file)
    if (resp && resp.url) {
      setLotoIcons(prev => ({ ...prev, [region]: resp.url }))
      return resp.url
    }
    return null
  }

  return (
    <div className="game-settings">
      <Card border="light" className="shadow-sm mb-4">
        <Card.Header className="game-settings-header">
          <div>
            <h5 className="mb-1">Cài đặt trò chơi ({listGame.total ?? '…'})</h5>
            <small className="text-muted">Bấm “Chỉnh 3 mốc” cạnh tên game để cấu hình mua, in và upload vé.</small>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {listGame.isOff > 0 && <Badge bg="danger">{listGame.isOff} không hoạt động</Badge>}
            {listGame.isOn > 0 && <Badge bg="success">{listGame.isOn} đang hoạt động</Badge>}
            <Button size="sm" variant="outline-secondary" disabled={loading} onClick={() => setRefreshKey(value => value + 1)}>
              Làm mới
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="game-settings-help mb-3" role="note" aria-label="Ý nghĩa ba mốc">
            <div><strong>Đóng mua</strong><p className="small text-muted">Khách ngừng mua vé cho kỳ đã chọn.</p></div>
            <div><strong>Đóng in</strong><p className="small text-muted">Nhân viên ngừng gửi lệnh in cho kỳ đó.</p></div>
            <div><strong>Đóng upload vé</strong><p className="small text-muted">Ngừng gắn ảnh / hoàn thành đơn, độc lập với giờ in.</p></div>
          </div>
          <p className="small text-muted">Danh sách hiển thị mốc đang dùng. Mở xem hoặc chỉnh ô chưa thay đổi cấu hình;
            chỉ áp dụng khi bấm <strong>Lưu và áp dụng 3 mốc</strong> trong cửa sổ cấu hình.</p>
          {loadError && <Alert variant="danger">{loadError}. Bấm “Làm mới” để tải lại.</Alert>}
          {loading ? <p role="status" className="text-muted mb-0">Đang tải cấu hình trò chơi…</p> :
            <div className="game-settings-list">
              {(listGame.data || []).map(game => (
                <GameSettingsRow
                  key={game.id}
                  {...game}
                  expanded={expandedGameId === game.id}
                  onToggle={() => setExpandedGameId(current => current === game.id ? null : game.id)}
                  onGuidePress={onGuidePress}
                  onCutoffDraftPress={setCutoffDraftGame}
                  onLotto535ConfigPress={() => setLotto535ConfigShow(true)}
                  onStatusChange={onStatusChange}
                  onOrderNumberChange={onOrderNumberChange}
                  onIconChange={onIconChange}
                  onIconUpload={onIconUpload}
                  onCutoffChange={onCutoffChange}
                  onPrintCutoffChange={onPrintCutoffChange}
                  onCutoffOffsetChange={onCutoffOffsetChange}
                  kenoPrintCutoffSeconds={kenoPrintCutoffSeconds}
                  onKenoPrintCutoffChange={onKenoPrintCutoffChange}
                />
              ))}
              {!listGame.data?.length && <p className="text-muted mb-0">Chưa tải được trò chơi nào.</p>}
            </div>}
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <details>
          <summary className="card-header game-settings-region-heading">Logo và hiển thị Lô tô theo miền</summary>
          <Card.Body>
            <p className="text-muted small">Phần này chỉ chỉnh logo / hiển thị. Giờ chốt MB / MT / MN nằm trong Cài đặt Lô tô.</p>
            {!lotoRegionReady && <p className="text-muted small">Chưa tải xong cấu hình miền; chưa thể đổi trạng thái.</p>}
            <div className="game-settings-regions">
              {LOTO_REGIONS.map(({ code, label }) => (
                <LotoIconRow key={code} code={code} label={label} icon={lotoIcons[code]}
                  enabled={lotoRegionConfig[code]} disabled={loading || !lotoRegionReady}
                  onIconChange={onLotoIconChange} onIconUpload={onLotoIconUpload}
                  onStatusChange={onLotoStatusChange} />
              ))}
            </div>
          </Card.Body>
        </details>
      </Card>

      <Card border="light" className="shadow-sm mb-4">
        <details>
          <summary className="card-header game-settings-region-heading">Hiển thị xổ số kiến thiết theo miền</summary>
          <Card.Body>
            <p className="text-muted small">Bật / ẩn miền trên trang chủ, không thay đổi giờ xổ hoặc lịch đài.</p>
            {!classicRegionReady && <p className="text-muted small">Chưa tải xong cấu hình miền; chưa thể đổi trạng thái.</p>}
            <div className="game-settings-regions">
              {CLASSIC_REGIONS.map(({ code, label }) => (
                <ClassicRegionRow key={code} code={code} label={label} enabled={classicRegionConfig[code]}
                  disabled={loading || !classicRegionReady} onStatusChange={onClassicStatusChange} />
              ))}
            </div>
          </Card.Body>
        </details>
      </Card>

      <UpdateGuide
        name={currentGameName}
        game={currentGameId}
        guide={currentGameGuide}
        loading={guideLoading}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
      <CutoffPolicyDraft
        show={Boolean(cutoffDraftGame)}
        game={cutoffDraftGame}
        token={cookies.lottmekong}
        onSaved={fetchGames}
        onHide={() => setCutoffDraftGame(null)}
      />
      <Lotto535Config
        cutoffManaged={listGame.data?.find(game => game.id === 'lotto535')?.cutoffPolicyEnabled === true}
        show={lotto535ConfigShow}
        onHide={() => setLotto535ConfigShow(false)}
        token={cookies.lottmekong}
        refreshKey={lotto535ConfigVersion}
      />
    </div>
  );
};
